import { CelestialBody } from '@/universe/CelestialBody';
import { SolarSystem } from '@/universe/SolarSystem';
import { TourConfig } from './TourConfig';

export interface TourStop {
  bodyId: string;
  /** Whether the stop includes a descent and low surface pass. Gas giants and other unlandable bodies get orbit only. */
  hasSurface: boolean;
}

export type TourPhase = 'transit' | 'orbit' | 'descend' | 'surface' | 'ascend';

/**
 * The full itinerary: each planet in turn, immediately followed by its own
 * moons (if `includeMoons`), before moving to the next planet. `bodyOrder`
 * overrides which planets are visited and in what order; moons always follow
 * their own parent regardless of `bodyOrder`.
 */
export function buildTourStops(system: SolarSystem, config: TourConfig): TourStop[] {
  const excluded = new Set(config.excludeBodyIds);

  const planets: CelestialBody[] =
    config.bodyOrder.length > 0
      ? config.bodyOrder
          .map((id) => system.find(id))
          .filter((b): b is CelestialBody => b !== undefined && b.def.kind !== 'moon')
      : system.planets;

  const stops: TourStop[] = [];
  for (const planet of planets) {
    if (excluded.has(planet.id)) continue;
    stops.push({ bodyId: planet.id, hasSurface: planet.def.terrain !== null });

    if (!config.includeMoons) continue;
    const moons = system.bodies.filter((m) => m.parent?.id === planet.id);
    for (const moon of moons) {
      if (excluded.has(moon.id)) continue;
      stops.push({ bodyId: moon.id, hasSurface: moon.def.terrain !== null });
    }
  }
  return stops;
}
